import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What should I wear?',
      answer: 'We\'re having a semi-formal celebration. Think cocktail attire - suits for men, cocktail dresses or elegant separates for women. Please avoid white, ivory, or cream colors as those are reserved for the bride!'
    },
    {
      question: 'Are children invited?',
      answer: 'While we absolutely adore your little ones, we\'ve decided to have an adults-only celebration to keep the atmosphere relaxed and intimate. We hope you understand and can arrange childcare for the day.'
    },
    {
      question: 'What time should I arrive?',
      answer: 'Please arrive by 2:30 PM for a 3:00 PM ceremony start. This gives everyone time to find parking, grab a welcome drink, and get settled before we begin.'
    },
    {
      question: 'Will there be parking available?',
      answer: 'Yes! The venue has a large car park with free parking for all guests. There\'s also overflow parking available if needed. Please don\'t park on the road outside the venue.'
    },
    {
      question: 'What about dietary requirements?',
      answer: 'We want everyone to enjoy the meal! Please let us know about any dietary requirements, allergies, or special needs when you RSVP. Our caterers can accommodate most dietary restrictions.'
    },
    {
      question: 'Is the ceremony outdoors?',
      answer: 'The ceremony will be held in our beautiful indoor chapel, so weather won\'t be a concern. The cocktail reception will be in the garden (weather permitting) with indoor backup space available.'
    },
    {
      question: 'Can I bring a plus one?',
      answer: 'Plus ones are specified on your invitation. If your invitation says "and guest" then yes, please bring someone! If not, we\'re keeping the celebration intimate and would prefer just you.'
    },
    {
      question: 'What time does the party end?',
      answer: 'The official celebration ends at midnight, but the party can continue as long as you\'re having fun! We\'ll have transport options available for those who need to leave earlier.'
    },
    {
      question: 'Should I take photos during the ceremony?',
      answer: 'We\'d love for you to enjoy the moment with us! Our photographer will capture all the key moments, so feel free to put your phones away during the ceremony and enjoy being present with us.'
    },
    {
      question: 'How do I get to the venue?',
      answer: 'Full directions and transport information are available on our Travel page. The venue address is provided there along with parking details and public transport options.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="bg-gradient-to-br from-cream via-white to-blush py-12 px-4" style={{ minHeight: '100vh', paddingBottom: '20vh' }}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h1 className="page-header text-charcoal mb-4">
            Frequently Asked Questions
          </h1>
          <p className="body-text text-gray-light">
            Everything you need to know about our special day
          </p>
        </motion.div>

        <div className="space-y-4 flex flex-col items-center">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg overflow-hidden w-3/4"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full p-6 text-left"
              >
                <div className="flex justify-between items-center">
                  <h3 className="subheading text-charcoal pr-4" style={{ fontSize: '1rem' }}>
                    {faq.question}
                  </h3>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 45 : 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-gold section-header flex-shrink-0"
                  >
                    +
                  </motion.div>
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 body-text text-gray-700" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-12 text-center p-6 bg-white/60 rounded-lg"
        >
          <h3 className="subheading text-charcoal mb-4">
            Have another question?
          </h3>
          <div className="body-text text-gray-700 space-y-2">
            <p>Contact us at <a href="mailto:contact@catandri.ch" className="text-gold hover:underline">contact@catandri.ch</a></p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;